import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, ShoppingBag, Zap, Trash2, Loader2 } from "lucide-react";
import { useCart } from "../context/CartContext";
import { formatINR } from "../data/products";
import { toast } from "sonner";
import { trackInitiateCheckout } from "../lib/metaPixel";

const ease = [0.22, 1, 0.36, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CartDrawer = () => {
  const { items, isOpen, closeCart, updateQty, removeItem, subtotal, count, clearCart } = useCart();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      if (window.__lenis) window.__lenis.stop();
    } else {
      document.body.style.overflow = "";
      if (window.__lenis) window.__lenis.start();
    }
    return () => {
      document.body.style.overflow = "";
      if (window.__lenis) window.__lenis.start();
    };
  }, [isOpen]);

  const handleCheckout = async () => {
    const lines = items
      .filter((i) => i.product.variantId)
      .map((i) => ({ variant_id: i.product.variantId, quantity: i.qty }));

    // No real Shopify variant IDs mapped yet — graceful fallback.
    if (lines.length === 0) {
      toast.info("Secure checkout is being connected to Shopify", {
        description: "Your festive box is saved and ready to redirect at launch.",
      });
      return;
    }

    setLoading(true);
    trackInitiateCheckout({
      items: items.map((i) => ({ id: i.product.id, quantity: i.qty })),
      value: subtotal,
    });
    try {
      const res = await fetch(`${API}/cart/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lines }),
      });
      const data = await res.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url; // Shopify hosted checkout
        return;
      }
      toast.info("Secure checkout is being connected to Shopify", {
        description: "Your festive box is saved and ready to redirect at launch.",
      });
    } catch (e) {
      toast.error("Couldn't reach checkout. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={closeCart}
            className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm"
            data-testid="cart-backdrop"
          />

          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease }}
            className="fixed right-0 top-0 z-[120] flex h-full w-full max-w-md flex-col border-l border-[#F3E5AB]/12 bg-[#0d1220]"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between border-b border-[#F3E5AB]/10 px-6 py-5">
              <div className="flex items-center gap-3">
                <ShoppingBag className="h-5 w-5 text-[#D4AF37]" strokeWidth={1.5} />
                <h2 className="font-serif text-2xl tracking-tight text-[#F3EEE3]">Your Festive Box</h2>
                <span className="font-sans text-sm text-[#8B93A7]">({count})</span>
              </div>
              <button
                onClick={closeCart}
                aria-label="Close cart"
                data-testid="cart-close-btn"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-[#F3E5AB]/12 text-[#8B93A7] transition-colors duration-300 hover:border-[#D4AF37] hover:text-[#D4AF37]"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6">
              {items.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center text-center">
                  <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full border border-[#F3E5AB]/12 bg-white/5">
                    <ShoppingBag className="h-8 w-8 text-[#8B93A7]" strokeWidth={1} />
                  </div>
                  <p className="font-serif text-2xl text-[#F3EEE3]">Your festive box is empty</p>
                  <p className="mt-2 font-sans text-sm text-[#8B93A7]">Add a touch of festive elegance to begin.</p>
                  <button
                    onClick={closeCart}
                    data-testid="cart-continue-shopping-btn"
                    className="mt-6 rounded-full border border-[#D4AF37]/40 px-6 py-3 font-sans text-sm text-[#F3E5AB] transition-colors duration-300 hover:border-[#D4AF37]"
                  >
                    Continue Shopping
                  </button>
                </div>
              ) : (
                <ul className="space-y-5">
                  {items.map((item) => (
                    <li key={item.product.id} className="flex gap-4" data-testid={`cart-item-${item.product.id}`}>
                      <div className="shimmer relative h-24 w-24 shrink-0 overflow-hidden rounded-lg">
                        <div className="absolute left-1/2 top-1/2 h-1/2 w-1/2 -translate-x-1/2 -translate-y-1/2 bloom-gold opacity-50" />
                      </div>

                      <div className="flex flex-1 flex-col">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="font-serif text-lg leading-tight text-[#F3EEE3]">{item.product.name}</h3>
                          <button
                            onClick={() => {
                              removeItem(item.product.id);
                              toast("Removed from festive box");
                            }}
                            aria-label="Remove item"
                            data-testid={`cart-remove-${item.product.id}`}
                            className="text-[#8B93A7] transition-colors duration-300 hover:text-[#A6324F]"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>

                        <div className="mt-1 flex items-center gap-1.5">
                          <Zap className="h-3 w-3 text-[#D4AF37]" fill="#D4AF37" strokeWidth={0} />
                          <span className="font-sans text-[10px] uppercase tracking-wider text-[#8B93A7]">Express 2-Day</span>
                        </div>

                        <div className="mt-auto flex items-center justify-between pt-3">
                          <div className="flex items-center rounded-full border border-[#F3E5AB]/15">
                            <button
                              onClick={() => updateQty(item.product.id, -1)}
                              aria-label="Decrease quantity"
                              data-testid={`cart-qty-decrease-${item.product.id}`}
                              className="flex h-8 w-8 items-center justify-center text-[#8B93A7] transition-colors duration-300 hover:text-[#D4AF37]"
                            >
                              <Minus className="h-3.5 w-3.5" />
                            </button>
                            <span className="w-8 text-center font-sans text-sm text-[#F3EEE3]" data-testid={`cart-qty-${item.product.id}`}>
                              {item.qty}
                            </span>
                            <button
                              onClick={() => updateQty(item.product.id, 1)}
                              aria-label="Increase quantity"
                              data-testid={`cart-qty-increase-${item.product.id}`}
                              className="flex h-8 w-8 items-center justify-center text-[#8B93A7] transition-colors duration-300 hover:text-[#D4AF37]"
                            >
                              <Plus className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          <span className="font-sans text-sm font-semibold text-[#D4AF37]">
                            {formatINR(item.product.price * item.qty)}
                          </span>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-[#F3E5AB]/10 px-6 py-6">
                <div className="mb-1 flex items-center justify-between">
                  <span className="font-sans text-sm text-[#8B93A7]">Subtotal</span>
                  <span className="font-serif text-2xl text-[#F3EEE3]" data-testid="cart-subtotal">{formatINR(subtotal)}</span>
                </div>
                <p className="mb-5 font-sans text-xs text-[#8B93A7]">Shipping & taxes calculated at checkout · Free express over ₹2,000</p>
                <button
                  onClick={handleCheckout}
                  disabled={loading}
                  data-testid="cart-checkout-btn"
                  className="btn-gold mb-3 flex w-full items-center justify-center gap-2 rounded-full py-4 font-sans text-sm font-semibold tracking-wide disabled:opacity-70"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Redirecting…
                    </>
                  ) : (
                    "Secure Checkout"
                  )}
                </button>
                <button
                  onClick={clearCart}
                  data-testid="cart-clear-btn"
                  className="w-full font-sans text-xs uppercase tracking-[0.15em] text-[#8B93A7] transition-colors duration-300 hover:text-[#F3EEE3]"
                >
                  Clear box
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;
